/* Home.tsx */
import React, { useRef, useState } from "react";

/* =========================
   Branding
========================= */
const BRAND_NAME = "OASIS AI";
const PLATFORM_NAME = "Enterprise AI Platform";

/* =========================
   Types
========================= */
type AccordionKey = "company" | "commerce" | "crm" | "dev";

/* =========================
   Component
========================= */
export default function Home() {
  const [open, setOpen] = useState<AccordionKey>("commerce");
  const [showDemo, setShowDemo] = useState(false);
  const [showMoreCompany, setShowMoreCompany] = useState(false);

  const companyRef = useRef<HTMLDivElement | null>(null);
  const commerceRef = useRef<HTMLDivElement | null>(null);
  const crmRef = useRef<HTMLDivElement | null>(null);
  const devRef = useRef<HTMLDivElement | null>(null);

  const toggle = (
    key: AccordionKey,
    ref: React.RefObject<HTMLDivElement | null>
  ) => {
    setOpen(prev => (prev === key ? prev : key));
    setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  return (
    <div style={styles.page}>
      {/* ================= HERO ================= */}
<section
  style={{
    ...styles.hero,
    position: "relative",
    overflow: "hidden",
    ...styles.fadeInUp,
  }}
>
  <div style={glossyOverlay} />

  <div style={{ position: "relative", zIndex: 1 }}>
    <span style={styles.badge}>{PLATFORM_NAME}</span>
    <h1 style={styles.heroTitle}>{BRAND_NAME}</h1>
    <h2 style={styles.heroSubtitle}>
      질문에 답하는 AI를 넘어<br />
      판단하고 제안하는 AI
    </h2>
    <p style={styles.heroDesc}>
      조직의 데이터와 맥락을 이해하고,<br />
      <strong>사람이 하던 사고와 판단을 함께 수행합니다.</strong>
    </p>
  </div>
</section>

      {/* ================= CORE VALUE ================= */}
      <section style={styles.core}>
        <h2 style={styles.sectionTitle}>
          Enterprise AI Platform<br />
          핵심은 사고력입니다
        </h2>

        <div style={styles.coreGrid}>
          <div style={styles.coreCard}>
            <div style={styles.coreIcon}>🧠</div>
            <h3 style={styles.coreTitle}>사고 · 추론</h3>
            <p style={styles.coreDesc}>
              단순 검색이 아닌,<br />
              정보를 조합해 상황과 맥락을 이해합니다.
            </p>
          </div>

          <div style={styles.coreCard}>
            <div style={styles.coreIcon}>⚖️</div>
            <h3 style={styles.coreTitle}>판단 · 제안</h3>
            <p style={styles.coreDesc}>
              사내 규정, 고객 이력, 데이터 기반으로<br />
              다음 행동을 제안합니다.
            </p>
          </div>

          <div style={styles.coreCard}>
            <div style={styles.coreIcon}>🚀</div>
            <h3 style={styles.coreTitle}>실행 가속</h3>
            <p style={styles.coreDesc}>
              사람의 생각 시간을 줄이고<br />
              결정과 실행 속도를 높입니다.
            </p>
          </div>
        </div>
      </section>

      {/* ================= USE CASE ================= */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>
          이렇게 활용됩니다
        </h2>
        <p style={styles.sectionDesc}>
          각 조직이 실제로 던지는 질문을 기준으로 설명합니다
        </p>


        <div style={styles.accordion}>
          {/* ================= 쇼핑몰 ================= */}
          <div ref={commerceRef} style={styles.accItem}>
            <AccordionHeader
              title="🛒 쇼핑몰 · 이커머스"
              open={open === "commerce"}
              onClick={() => toggle("commerce", commerceRef)}
            />
            {open === "commerce" && (
              <AccordionBody>
                <p style={styles.blockTitle}>
                  주문 데이터 분석 + 구매 패턴 추론 + 행동 제안
                </p>
                <ul style={styles.list}>
                  <li>주문 · 배송 · 교환 · 반품 자동 응답</li>
                  <li>구매 이력 기반 고객 성향 분석</li>
                  <li>상품리뷰 내용 분석</li>             
                  <li>재구매 주기 예측</li>                  
                  <li>추천 상품 및 메시지 시점 제안</li>
                  <li>반품 사유 분석 및 상품 개선 인사이트</li>
                </ul>

                <Example>
                  <strong>Q.</strong>이 상품의 3개월간 리뷰 분석해줘<br />
                  <strong>A.</strong> 긍정적인 피드백:
                  제품 품질: 리뷰의 약 60%가 제품의 품질에 대해 긍정적인 평가를 했습니다. 특히 내구성과 디자인 면에서 좋은 점수를 받았습니다.<br/>
                  배송 속도: 30%의 리뷰에서 빠른 배송에 만족감을 나타냈으며, 예정보다 일찍 도착했다는 피드백이 있었습니다.<br/>
                  <br/>
                  부정적인 피드백:<br/>
                  가격: 약 20%의 리뷰가 가격이 다소 비싸다고 느꼈다는 의견을 제시했습니다.<br/>
                  사이즈 이슈: 15%의 리뷰에서 사이즈가 예상과 다르다는 불만이 있었습니다. 이는 주로 의류나 신발 상품에서 발생하는 문제입니다.<br/>
                  기타 피드백:<br/>
                  고객 서비스: 몇몇 리뷰에서 고객 서비스의 응답 속도가 빠르며, 문제 해결이 원활하다는 긍정적인 평가를 받았습니다.<br/><br/>
                  제안:<br/>
                  사이즈 가이드 개선: 사이즈 관련 불만이 반복되므로, 상세한 사이즈 가이드를 제공하거나, 교환/반품이 용이하도록 제도를 개선하는 것이 좋습니다.<br/>
                  프로모션 전략: 가격에 대한 불만이 있는 만큼, 할인 쿠폰이나 적립 포인트를 통한 프로모션을 고려해 볼 수 있습니다.<br/>
                  고객 리뷰 강조: 긍정적인 리뷰를 적극적으로 홍보하여 신규 고객에게 신뢰를 줄 수 있습니다.<br/>

                </Example>

                <Example>
                  <strong>Q.</strong> 이 상품은 왜 장바구니에만 담기고 구매가 안 될까요?<br />
                  <strong>A.</strong> 가격 대비 후기 수가 적고
                  배송 정보가 구매 단계 후반에 노출되고 있어
                  구매 결정을 망설이게 만드는 요인으로 판단됩니다.
                </Example>


                <Example>                  
                  <strong>Q.</strong> 이 고객에게 지금 어떤 상품을 추천하면 좋을까요?<br />
                  <strong>A.</strong> 최근 6개월간 동일 카테고리 상품을 3회 반복 구매했으며,
                  평균 재구매 주기는 약 45일입니다.
                  현재 재구매 가능성이 높은 상태로,
                  동일 브랜드 신상품 추천이 적합합니다.
                </Example>

                <Example>
                  <strong>Q.</strong> 언제 메시지를 보내는 게 좋을까요?<br />
                  <strong>A.</strong> 마지막 구매 후 42일이 경과했으며,
                  고객의 평균 재구매 시점에 근접해 있습니다.
                  할인 또는 재입고 알림 메시지가 효과적일 것으로 판단됩니다.
                </Example>

                {showMoreCompany && (
                  <>
                  <Example>
                    <strong>Q.</strong> 할인 없이도 구매 전환이 가능할까요?<br />
                    <strong>A.</strong> 해당 고객은 가격보다
                    배송 속도와 재구매 편의성을 중시하는 성향을 보입니다.
                  </Example>

                  <Example>
                    <strong>Q.</strong> 반품이 잦은 고객은 어떻게 관리하는 게 좋을까요?<br />
                    <strong>A.</strong> 사이즈 관련 반품 비율이 높아
                    상세 사이즈 가이드 강화가 효과적인 대응으로 판단됩니다.
                  </Example>
                  </>
                )}

              <button
                style={styles.moreBtn}
                onClick={() => setShowMoreCompany(v => !v)}
              >
                {showMoreCompany ? "접기" : "+ 더보기"}
              </button>
              </AccordionBody>
            )}
          </div>


          {/* ================= 일반기업 ================= */}
          <div ref={companyRef} style={styles.accItem}>
            <AccordionHeader
              title="🏢 사내 운영 · 관리 영역"
              open={open === "company"}
              onClick={() => toggle("company", companyRef)}
            />
            {open === "company" && (
              <AccordionBody>
                <p style={styles.blockTitle}>
                  사내 질문 응답 + 판단 + 문서 작성
                </p>
                <ul style={styles.list}>
                  <li>연차 · 휴가 · 병가 · 대체휴무 기준 안내</li>
                  <li>연말정산 · 급여 · 상여 · 수당 문의 응답</li>
                  <li>출장비 · 법인카드 · 구매 프로세스 설명</li>
                  <li>사내 정책 · 규정 · 공지 자동 응답</li>
                  <li>부서별 담당자 분류 및 안내</li>
                  <li>기안문 · 내부 문서 초안 작성</li>
                </ul>

                <Example>
                  <strong>Q.</strong> 노트북 교체 관련 기안문 초안 만들어줘.<br />
                  <strong>A.</strong> 최근 신규 입사자 증가로 인해
                  업무용 노트북 수요가 증가하고 있습니다.
                  이에 따라 장비 노후화로 인한 업무 효율 저하를 방지하기 위해
                  노트북 교체를 요청드립니다.
                </Example>

                <Example>
                  <strong>Q.</strong> 이 상황은 병가로 처리하는 게 맞을까요?<br />
                  <strong>A.</strong> 사내 규정 기준으로 2일 이내 병가는 증빙 없이 가능합니다.
                  현재 상황에서는 병가 처리 후,
                  추가 사용 시 진단서 제출을 안내하는 것이 적절합니다.
                </Example>

                {showMoreCompany && (
                  <>

                    <Example>
                      <strong>Q.</strong> 사내 문의가 특정 부서로만 집중되는 이유는 뭘까요?<br />
                      <strong>A.</strong> 공식 가이드 부재로 인해
                      비공식적으로 ‘아는 사람에게 묻는 구조’가 형성되어 있습니다.
                    </Example>

                    <Example>
                      <strong>Q.</strong> 이 팀은 요즘 야근이 많은데 구조적인 문제일까요?<br />
                      <strong>A.</strong> 최근 3개월간 해당 팀의 업무 요청 건수가 평균 대비 28% 증가했습니다.
                      인력 증원 없이 업무량이 늘어난 상태로,
                      일시적 이슈보다는 구조적 과부하로 판단됩니다.
                    </Example>

                    <Example>
                      <strong>Q.</strong> 이 팀은 요즘 야근이 많은데 구조적인 문제일까요?<br />
                      <strong>A.</strong> 최근 3개월간 해당 팀의 업무 요청 건수가 평균 대비 28% 증가했습니다.
                      인력 증원 없이 업무량이 늘어난 상태로,
                      일시적 이슈보다는 구조적 과부하로 판단됩니다.
                    </Example>

                    <Example>
                      <strong>Q.</strong> 신규 입사자 온보딩이 계속 지연되는 이유가 뭘까요?<br />
                      <strong>A.</strong> 온보딩 자료가 부서별로 분산되어 있고
                      담당자 의존도가 높아 반복 지연이 발생하고 있습니다.
                      공통 가이드 정리와 자동 안내 프로세스가 필요합니다.
                    </Example>
            
                  </>
                )}

              <button
                style={styles.moreBtn}
                onClick={() => setShowMoreCompany(v => !v)}
              >
                {showMoreCompany ? "접기" : "+ 더보기"}
              </button>

              </AccordionBody>
            )}
          </div>



          {/* ================= CRM ================= */}
          <div ref={crmRef} style={styles.accItem}>
            <AccordionHeader
              title="📊 CRM · 영업 · 고객 상담"
              open={open === "crm"}
              onClick={() => toggle("crm", crmRef)}
            />
            {open === "crm" && (
              <AccordionBody>
                <p style={styles.blockTitle}>
                  고객 맥락 이해 + 심리 추론 + 응답 제안
                </p>
                <ul style={styles.list}>
                  <li>고객 이력 자동 요약</li>
                  <li>이전 상담 · 계약 · 이슈 정리</li>
                  <li>고객 감정 및 불만 강도 추론</li>
                  <li>적절한 응답 톤 및 문구 제안</li>
                  <li>제안서 · 답변 초안 생성</li>
                </ul>

                <Example>
                  <strong>Q.</strong> 이 고객 문의에 어떻게 답하는 게 좋을까요?<br />
                  <strong>A.</strong> 문의 내용에서 배송 지연에 대한 불만이 강하게 나타납니다.
                  먼저 사과를 명확히 전달한 후,
                  보상 정책을 함께 안내하는 답변이 효과적입니다.
                </Example>

                {showMoreCompany && (
                  <>
                  <Example>
                    <strong>Q.</strong> 이 고객은 왜 답변에 민감하게 반응할까요?<br />
                    <strong>A.</strong> 과거 보상 지연 경험으로 인해
                    브랜드 신뢰도가 낮아진 상태로 추론됩니다.
                  </Example>

                  <Example>
                    <strong>Q.</strong> 이 문의는 왜 해결까지 시간이 오래 걸릴까요?<br />
                    <strong>A.</strong> 내부 승인 권한이 분산되어 있어
                    처리 경로가 길어지는 구조적 원인이 확인됩니다.
                  </Example>

                  </>
                )}

              <button
                style={styles.moreBtn}
                onClick={() => setShowMoreCompany(v => !v)}
              >
                {showMoreCompany ? "접기" : "+ 더보기"}
              </button>
              </AccordionBody>
            )}
          </div>

          {/* ================= 개발 ================= */}
          <div ref={devRef} style={styles.accItem}>
            <AccordionHeader
              title="💻 개발 · IT 조직"
              open={open === "dev"}
              onClick={() => toggle("dev", devRef)}
            />
            {open === "dev" && (
              <AccordionBody>
                <p style={styles.blockTitle}>
                  기술 맥락 이해 + 구조적 사고 정리
                </p>
                <ul style={styles.list}>
                  <li>프로젝트 구조 · 기술 스택 설명</li>
                  <li>배포 · 장애 대응 가이드</li>
                  <li>운영 히스토리 기반 질의응답</li>
                  <li>신규 개발자 온보딩 지원</li>
                </ul>
                
                <Example>
                  <strong>Q.</strong> 신규 개발자가 이 구조를 어려워하는 이유는 뭘까요?<br />
                  <strong>A.</strong> 도메인 설명 없이
                  코드 중심 문서만 제공되어 이해 장벽이 높습니다.
                </Example>

                <Example>
                  <strong>Q.</strong> 왜 이 서비스는 이런 구조를 쓰고 있나요?<br />
                  <strong>A.</strong> 트래픽 변동이 크기 때문에
                  프론트엔드와 백엔드를 분리해 확장성을 확보했습니다.
                  또한 배포 시 리스크를 줄이기 위한 구조입니다.
                </Example>

                {showMoreCompany && (
                  <>
                    <Example>
                      <strong>Q.</strong> 배포 주기가 점점 길어지는 이유는 무엇일까요?<br />
                      <strong>A.</strong> 테스트 범위가 명확하지 않아
                      검증 시간이 지속적으로 증가하고 있습니다.
                    </Example>

                    <Example>
                      <strong>Q.</strong> 이 기술 부채는 지금 해결하는 게 맞을까요?<br />
                      <strong>A.</strong> 현재 트래픽 규모에서는 영향이 제한적이나
                      향후 트래픽 증가 시 리스크가 커질 가능성이 있습니다.
                    </Example>

                    <Example>
                      <strong>Q.</strong> 이 API는 왜 사용량이 줄었을까요?<br />
                      <strong>A.</strong> 유사 기능의 신규 API가 추가되며
                      역할이 중복된 상태로 판단됩니다.
                    </Example>
                  </>
                )}

              <button
                style={styles.moreBtn}
                onClick={() => setShowMoreCompany(v => !v)}
              >
                {showMoreCompany ? "접기" : "+ 더보기"}
              </button>
              </AccordionBody>
            )}
          </div>
        </div>
      </section>

      {/* ================= EFFECT ================= */}
      <section style={styles.sectionAlt}>
        <h2 style={styles.sectionTitle}>
          AI를 도입하면,<br />
          조직은 이렇게 달라집니다
        </h2>

        <p style={styles.sectionDesc}>
          Enterprise AI Platform은<br />사람을 대체하지 않습니다.<br />
          대신, 사람이 가장 잘할 수 있는 일에 <br />집중할 수 있도록 돕습니다.
        </p>

        <div style={styles.effectGrid}>
          {/* 반복 업무 감소 */}
          <div style={styles.effectCard}>
            <h3 style={styles.effectTitle}>① 반복 업무가 줄어듭니다</h3>
            <p style={styles.effectDesc}>
              사내 규정 질문, 고객 문의, 주문·배송·교환 응대 등<br />
              이미 답이 정해진 질문들은 AI가 먼저 처리합니다.
            </p>
            <p style={styles.effectSub}>
              조직 내 질문의 대부분은<br />
              “이미 한 번은 답해본 질문”입니다.
            </p>
          </div>

          {/* 사고 시간 감소 */}
          <div style={styles.effectCard}>
            <h3 style={styles.effectTitle}>② 생각의 초안을 AI가 만듭니다</h3>
            <p style={styles.effectDesc}>
              기안문, 제안서, 고객 응대 문구, 판단이 필요한 질문까지<br />
              AI가 사고의 초안을 만들어줍니다.
            </p>
            <p style={styles.effectSub}>
              AI는 결정을 대신하지 않습니다.<br />
              결정을 더 빠르게 할 수 있게 도와줍니다.
            </p>
          </div>

          {/* 사람의 역할 변화 */}
          <div style={styles.effectCard}>
            <h3 style={styles.effectTitle}>③ 사람은 더 중요한 일에 집중합니다</h3>
            <p style={styles.effectDesc}>
              반복 설명과 정리에서 벗어나<br />
              전략, 고객 관계, 제품 개선에 집중할 수 있습니다.
            </p>
            <p style={styles.effectSub}>
              같은 인원으로 더 많은 일을 할 수 있는 구조,<br />
              그것이 AI 도입의 핵심 효과입니다.
            </p>
          </div>
        </div>

        {/* 키오스크 비유 */}
        <div style={styles.kioskBox}>
          <strong>키오스크를 떠올려보세요.</strong>
          <p style={{ marginTop: 8 }}>
            키오스크는 직원을 줄이기 위해 도입된 것이 아닙니다.<br />
            직원이 더 중요한 응대와 서비스에 집중할 수 있도록 단순 주문을 자동화한 것입니다.
          </p>
          <p style={{ marginTop: 8, fontWeight: 600 }}>
            Enterprise AI Platform도 같은 역할을 합니다.
          </p>
          <div style={styles.handshakeWrap}>
            <img
              src="https://backend-lab.com/img_2.jpg"
              alt="Human and AI working together"
              style={styles.handshakeImage}
            />
            <p style={{ marginTop: 12, fontSize: 13, opacity: 0.85 }}>
              AI와 사람이 함께 일하는 조직을 만듭니다.
            </p>
          </div>
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section style={styles.cta}>
        <h2 style={styles.ctaTitle}>
          우리 회사에 맞는 AI 활용<br />
          어디서부터 시작할까요
        </h2>
        <p style={styles.ctaDesc}>
          그 질문에 대한 답을<br />
          {PLATFORM_NAME}이 함께 찾습니다.
        </p>

        <div style={styles.ctaButtons}>
          <button
            style={styles.kakaoBtn}
            onClick={() =>
              window.open("https://open.kakao.com/o/sIsDq4ci/", "_blank")
            }
          >
            카카오톡으로 상담하기
          </button>
        </div>
      </section>

      {/* ================= FLOATING DEMO ================= */}
      <div style={styles.floatingDemoWrap}>
        <button
          style={styles.floatingDemoBtn}
          onClick={() => setShowDemo(prev => !prev)}
        >
          데모 보기
        </button>

        <div
          style={{
            ...styles.floatingDemoList,
            ...(showDemo ? styles.floatingDemoListOpen : {}),
          }}
        >
          <DemoItem
            label="🛒 쇼핑몰 고객용 AI"
            onClick={() => window.open("/cstmr", "_blank")}
          />
          <DemoItem
            label="📈 쇼핑몰 관리자 AI"
            onClick={() => window.open("/shop", "_blank")}
          />
          <DemoItem
            label="🏢 기업용 AI"
            onClick={() => window.open("/crm", "_blank")}
          />
          <DemoItem
            label="💬 일반 챗봇 AI"
            onClick={() => window.open("/chat", "_blank")}
          />
        </div>
      </div>

      {/* ================= FOOTER ================= */}
      <footer style={styles.footer}>
        © BackendLab. All rights reserved.
      </footer>
    </div>
  );
}

/* =========================
   Sub Components
========================= */
type AccordionHeaderProps = {
  title: string;
  open: boolean;
  onClick: () => void;
};

function AccordionHeader({ title, open, onClick }: AccordionHeaderProps) {
  return (
    <div style={styles.accHeader} onClick={onClick}>
      <span>{title}</span>
      <span>{open ? "−" : "+"}</span>
    </div>
  );
}

function AccordionBody({ children }: { children: React.ReactNode }) {
  return <div style={styles.accBody}>{children}</div>;
}

function Example({ children }: { children: React.ReactNode }) {
  return <div style={styles.example}>{children}</div>;
}

function DemoItem({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <div
      style={styles.demoItem}
      onClick={onClick}
      onMouseEnter={e =>
        (e.currentTarget.style.background = "rgba(0,0,0,0.04)")
      }
      onMouseLeave={e =>
        (e.currentTarget.style.background = "transparent")
      }
    >
      {label}
    </div>
  );
}

/* =========================
   Styles
========================= */

const glossyOverlay: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  background:
    "linear-gradient(120deg, rgba(255,255,255,0.35), rgba(255,255,255,0.05) 40%, rgba(255,255,255,0.15) 60%, rgba(255,255,255,0.05))",
  opacity: 0.6,
  pointerEvents: "none",
  animation: "glossyMove 6s ease-in-out infinite",
  zIndex: 0,
};

const glassCard: React.CSSProperties = {
  background: "rgba(255,255,255,0.65)",
  backdropFilter: "blur(28px)",
  WebkitBackdropFilter: "blur(28px)",
  border: "1px solid rgba(255,255,255,0.35)",
  boxShadow:
    "0 10px 30px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.4)",
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    fontFamily: "Pretendard, -apple-system, BlinkMacSystemFont, Arial, sans-serif",
    color: "#0f172a",
    lineHeight: 1.65,
    background:
      "linear-gradient(180deg, #eef2ff 0%, #ffffff 40%, #f8fafc 100%)",
  },

  /* ================= HERO ================= */
  hero: {
    padding: "160px 20px 140px",
    textAlign: "center",
  },

  badge: {
    display: "inline-block",
    padding: "8px 16px",
    borderRadius: 999,
    background: "rgba(15,23,42,0.85)",
    color: "#fff",
    fontSize: 13,
    marginBottom: 28,
    letterSpacing: 0.3,
  },

  heroTitle: {
    fontSize: 48,
    fontWeight: 800,
    letterSpacing: -0.5,
  },

  heroSubtitle: {
    fontSize: 26,
    marginTop: 18,
    fontWeight: 600,
    color: "#1e293b",
  },

  heroDesc: {
    marginTop: 26,
    fontSize: 18,
    color: "#475569",
  },

  /* ================= SECTION ================= */
  section: {
    padding: "110px 20px",
    maxWidth: 1200,
    margin: "0 auto",
  },

  sectionAlt: {
    padding: "120px 20px",
    background: "linear-gradient(180deg, #f8fafc, #eef2ff)",
  },

  sectionTitle: {
    fontSize: 34,
    fontWeight: 800,
    textAlign: "center",
    letterSpacing: -0.4,
  },

  sectionDesc: {
    textAlign: "center",
    color: "#475569",
    marginTop: 18,
    marginBottom: 70,
    fontSize: 17,
  },

  /* ================= CORE ================= */
  core: {
    padding: "110px 20px",
  },

  coreGrid: {
    marginTop: 70,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 36,
    maxWidth: 1100,
    marginLeft: "auto",
    marginRight: "auto",
  },

  coreCard: {
    ...glassCard,
    textAlign: "center",
    padding: "42px 28px",
    borderRadius: 24,
    transition: "transform 0.35s ease, box-shadow 0.35s ease",
  },

  coreCardHover: {
    transform: "translateY(-6px)",
    boxShadow: "0 24px 60px rgba(0,0,0,0.12)",
  },

  coreIcon: {
    fontSize: 52,
    marginBottom: 18,
  },

  coreTitle: {
    fontSize: 21,
    fontWeight: 700,
    marginBottom: 12,
  },

  coreDesc: {
    fontSize: 15,
    color: "#475569",
  },

  /* ================= ACCORDION ================= */
  accordion: {
    // maxWidth: 900,
    margin: "0 auto",
  },

  accItem: {
    ...glassCard,
    borderRadius: 22,
    marginBottom: 24,
  },

  accHeader: {
    padding: "22px 26px",
    cursor: "pointer",
    fontWeight: 700,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  accBody: {
    padding: "26px 30px",
    animation: "fadeIn 0.3s ease-out",
  },

  blockTitle: {
    fontWeight: 700,
    marginBottom: 14,
  },

  list: {
    paddingLeft: 18,
    marginBottom: 18,
  },

  example: {
    background: "rgba(15,23,42,0.05)",
    padding: "14px 16px",
    borderRadius: 12,
    fontSize: 14,
    marginBottom: 14,
  },

  /* ================= EFFECT ================= */
  effectGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 28,
    maxWidth: 1100,
    margin: "70px auto 0",
  },

  effectCard: {
    ...glassCard,
    borderRadius: 24,
    padding: "34px 30px",
  },

  effectTitle: {
    fontSize: 20,
    fontWeight: 700,
    marginBottom: 14,
  },

  effectDesc: {
    fontSize: 15,
    color: "#1e293b",
    marginBottom: 12,
  },

  effectSub: {
    fontSize: 14,
    color: "#475569",
  },

  /* ================= KIOSK ================= */
  kioskBox: {
    ...glassCard,
    maxWidth: 1040,
    margin: "90px auto 0",
    padding: "36px 34px",
    borderRadius: 28,
    fontSize: 15,
  },

  handshakeWrap: {
    marginTop: 28,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },

  handshakeImage: {
    maxWidth: 360,
    width: "100%",
    borderRadius: 20,
    boxShadow: "0 16px 36px rgba(0,0,0,0.12)",
  },

  /* ================= CTA ================= */
  cta: {
    padding: "140px 20px",
    textAlign: "center",
    background: "linear-gradient(180deg, #0f172a, #020617)",
    color: "#fff",
  },

  ctaTitle: {
    fontSize: 36,
    fontWeight: 800,
  },

  ctaDesc: {
    marginTop: 22,
    color: "#cbd5f5",
    fontSize: 18,
  },

  ctaButtons: {
    marginTop: 46,
    display: "flex",
    justifyContent: "center",
    gap: 18,
    flexWrap: "wrap",
  },

  kakaoBtn: {
    padding: "16px 38px",
    borderRadius: 999,
    background: "#FEE500",
    border: "none",
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
  },

  demoBtn: {
    padding: "16px 38px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.15)",
    color: "#fff",
    border: "1px solid rgba(255,255,255,0.3)",
    fontWeight: 600,
    cursor: "pointer",
    backdropFilter: "blur(12px)",
  },

  footer: {
    padding: "26px 12px",
    textAlign: "center",
    fontSize: 14,
    color: "#64748b",
    background: "#f8fafc",
  },
  fadeInUp: {
    animation: "fadeInUp 0.9s ease-out both",
  },

  demoSlide: {
    position: "absolute",
    top: "58px",
    left: "50%",
    transform: "translateX(-50%) translateY(-6px)",
    width: 220,
    background: "rgba(255,255,255,0.92)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    borderRadius: 18,
    boxShadow: "0 24px 50px rgba(0,0,0,0.18)",
    overflow: "hidden",
    opacity: 0,
    pointerEvents: "none",
    transition: "all 0.35s ease",
    zIndex: 10,
  },

  demoSlideOpen: {
    opacity: 1,
    transform: "translateX(-50%) translateY(0)",
    pointerEvents: "auto",
  },

  demoItem: {
    padding: "14px 18px",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    color: "#0f172a",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
  },

  moreBtn: {
    marginTop: 8,
    background: "transparent",
    border: "none",
    color: "#2563eb",
    fontWeight: 600,
    cursor: "pointer",
  },

  floatingDemoWrap: {
    position: "fixed",
    right: 24,
    bottom: 24,
    zIndex: 1000,
  },

  floatingDemoBtn: {
    padding: "16px 22px",
    borderRadius: 999,
    background:
      "linear-gradient(135deg, #6366f1, #3b82f6)",
    color: "#fff",
    fontWeight: 700,
    border: "none",
    cursor: "pointer",
    boxShadow:
      "0 18px 40px rgba(59,130,246,0.45)",
    backdropFilter: "blur(12px)",
  },

  floatingDemoList: {
    position: "absolute",
    bottom: "calc(100% + 14px)", // 버튼 바로 위
    right: 0,

    width: 240,
    background: "rgba(255,255,255,0.96)",
    backdropFilter: "blur(22px)",
    WebkitBackdropFilter: "blur(22px)",

    borderRadius: 20,
    boxShadow: "0 28px 60px rgba(0,0,0,0.22)",
    overflow: "hidden",

    opacity: 0,
    transform: "translateY(12px) scale(0.98)",
    pointerEvents: "none",

    transition: "all 0.32s cubic-bezier(0.4, 0, 0.2, 1)",
  },

  floatingDemoSlide: {
    position: "absolute",
    bottom: "64px",
    right: 0,
    width: 230,
    background: "rgba(255,255,255,0.95)",
    backdropFilter: "blur(22px)",
    WebkitBackdropFilter: "blur(22px)",
    borderRadius: 20,
    boxShadow: "0 30px 60px rgba(0,0,0,0.2)",
    overflow: "hidden",
    opacity: 0,
    pointerEvents: "none",
    transform: "translateY(10px)",
    transition: "all 0.35s ease",
  },

  floatingDemoListOpen: {
    opacity: 1,
    transform: "translateY(0) scale(1)",
    pointerEvents: "auto",
  },


};
